<template>
  <div>
    <modal ref="modal" modal-id="modal-authentication" size="md" show-on-mount headerless
           paddingless-body :click-outside-to-close="closeable" @visibility-changed="handleVisibilityChanged">
      <template #body>
        <div class="authentication-header bg-primary-translucent text-center py-4">
          <div class="px-3 offset-md-2 col-md-8">
            <slot name="header" />

            <facebook-sign-in v-if="enableFacebookLogin" :login-url="facebookLoginUrl" />
            <google-sign-in v-if="enableGoogleLogin" :login-url="googleLoginUrl" />
          </div>
        </div>

        <tabs :active-tab="activeTab" class-for-active="border-primary" :options="labeledTabs" @select="switchTab" />
        <component :is="activeForm" v-bind="formProperties"
                   class="px-3 px-md-4"
                   @authenticated="authenticated"
                   @authenticity-token-updated="authenticityTokenUpdated"
                   @awaitingEmailConfirmation="awaitingEmailConfirmation"
                   @errorOnSignUp="errorOnSignUp"
                   @mfa-setup-required="mfaSetupRequired"
                   @mfa-token-required="mfaTokenRequired"
                   @missing-info-required="missingInfoRequired" />
      </template>
    </modal>
  </div>
</template>

<script>
import FacebookSignIn from './facebook-sign-in'
import GoogleSignIn from './google-sign-in'
import { inject } from 'vue'
import Modal from '../components/modal'
import SignInForm from './sign-in-form'
import SignUpForm from './sign-up-form'
import Tabs from '../components/tabs'

const DEFAULT_FORGOT_PASSWORD_URL = '/users/password/new'
const DEFAULT_SIGN_IN_PATH = '/users/sign_in'
const DEFAULT_SIGN_UP_PATH = '/users'

export default {
  components: { FacebookSignIn, GoogleSignIn, Modal, SignInForm, SignUpForm, Tabs },

  props: {
    askForCountry: {
      type: Boolean,
      required: true
    },

    additionalFieldConfigs: {
      type: Array,
      required: true
    },

    askForPostcode: {
      type: Boolean,
      required: true
    },

    askForSmsOptIn: {
      type: Boolean,
      required: true
    },

    closeable: {
      type: Boolean,
      required: false,
      default: false
    },

    countryData: {
      type: Object,
      required: false
    },

    emailOptInTypeData: {
      type: Object,
      required: true
    },

    emailOptInLabel: {
      type: String,
      required: true
    },

    enableFacebookLogin: {
      type: Boolean,
      required: false,
      default: false
    },

    enableGoogleLogin: {
      type: Boolean,
      required: false,
      default: false
    },

    euDataProcessingConsentData: {
      type: Object,
      required: false
    },

    facebookLoginUrl: {
      type: String,
      required: false
    },

    forgotPasswordUrl: {
      type: String,
      required: false,
      default: DEFAULT_FORGOT_PASSWORD_URL
    },

    googleLoginUrl: {
      type: String,
      required: false
    },

    newAccountDisclaimer: {
      type: String,
      required: false
    },

    organisationId: {
      type: Number,
      required: true
    },

    phoneNumberLabel: {
      type: String,
      required: true
    },

    phoneNumberMandatory: {
      type: Boolean,
      required: true
    },

    postcodeLabel: {
      type: String,
      required: false
    },

    showEuDataProcessingConsent: {
      type: Boolean,
      required: true
    },

    signInButtonText: {
      type: String,
      required: false,
      default: function() { return inject('i18n').t('member.account.authentication_modal.sign_in_submit') }
    },

    signInPath: {
      type: String,
      required: false,
      default: DEFAULT_SIGN_IN_PATH
    },

    signUpButtonText: {
      type: String,
      required: false,
      default: function() { return inject('i18n').t('member.account.authentication_modal.sign_up_submit') }
    },

    signUpPath: {
      type: String,
      required: false,
      default: DEFAULT_SIGN_UP_PATH
    },

    smsOptInLabel: {
      type: String,
      required: false
    },

    smsOptInTypeData: {
      type: Object,
      required: false
    },

    termsOfServiceData: {
      type: Object,
      required: true
    }
  },

  emits: ['authenticated', 'awaitingEmailConfirmation', 'errorOnSignUp', 'mfa-token-required', 'missing-info-required',
          'modal-closed'],

  data() {
    return {
      activeTab: 'signUp',
      hasAuthenticated: false,
      labeledTabs: [
        { value: 'signUp', label: this.i18n.t('member.account.authentication_modal.create_account') },
        { value: 'signIn', label: this.i18n.t('member.account.authentication_modal.sign_in') }
      ]
    }
  },

  computed: {
    activeForm() {
      switch (this.activeTab) {
      case 'signUp':
        return SignUpForm
      case 'signIn':
        return SignInForm
      default:
        throw `Unexpected active tab ${this.activeTab}`
      }
    },

    formProperties() {
      let componentProperties
      switch (this.activeTab) {
      case 'signUp':
        componentProperties = {
          acceptTermsOfServiceLabel: this.termsOfServiceData.label,
          additionalFieldConfigs: this.additionalFieldConfigs,
          askForCountry: this.askForCountry,
          askForPostcode: this.askForPostcode,
          askForSmsOptIn: this.askForSmsOptIn,
          emailOptInTypeData: this.emailOptInTypeData,
          emailOptInLabel: this.emailOptInLabel,
          euDataProcessingConsentData: this.euDataProcessingConsentData,
          includeEuDataProcessingConsent: this.showEuDataProcessingConsent,
          newAccountDisclaimer: this.newAccountDisclaimer,
          phoneNumberLabel: this.phoneNumberLabel,
          phoneNumberMandatory: this.phoneNumberMandatory,
          signUpPath: this.signUpPath,
          smsOptInTypeData: this.smsOptInTypeData,
          smsOptInLabel: this.smsOptInLabel,
          submitButtonText: this.signUpButtonText,
          termsOfServiceExplicit: this.termsOfServiceData.displayExplicitCheckbox
        }

        if (this.askForPostcode) {
          componentProperties['postcodeLabel'] = this.postcodeLabel
        }

        if (this.countryData) {
          componentProperties['countryLabel'] = this.countryData.label
          componentProperties['countryOptions'] = this.countryData.countryOptions
          componentProperties['countryTogglesEuDataProcessingConsent'] = this.countryData.countryTogglesEuDataProcessingConsent
        }

        break
      case 'signIn':
        componentProperties = {
          forgotPasswordUrl: this.forgotPasswordUrl,
          organisationId: this.organisationId,
          signInPath: this.signInPath,
          submitButtonText: this.signInButtonText
        }
        break
      default:
        throw `Unexpected active tab ${this.activeTab}`
      }

      return componentProperties
    }
  },

  methods: {
    authenticated({ userMenu }) {
      // Replace the login link in the header with the user's menu.
      // We aren't using teleport because it's not possible to set dynamic
      // HTML content (v-html is not supported)
      const loginLinkWrapper = document.querySelector('#login-link')
      if (loginLinkWrapper) {
        loginLinkWrapper.innerHTML = userMenu
      }

      this.$refs.modal.close()
      this.$emit('authenticated')
    },

    authenticityTokenUpdated(newAuthenticityToken) {
      const tokenElement = document.querySelector('meta[name="csrf-token"]')
      tokenElement.setAttribute('content', newAuthenticityToken)
    },

    awaitingEmailConfirmation(userConfirmationData) {
      this.$refs.modal.close()
      this.$emit('awaitingEmailConfirmation', userConfirmationData)
    },

    errorOnSignUp({ errorMessage }) {
      this.$refs.modal.close()
      this.$emit('errorOnSignUp', errorMessage)
    },

    handleVisibilityChanged(visibility) {
      if (visibility === 'hidden') {
        this.$emit('modal-closed')
      }
    },

    mfaSetupRequired({ enableMfaPath }) {
      window.location.href = enableMfaPath
    },

    mfaTokenRequired(mfaParmameters) {
      this.$refs.modal.close()
      this.$emit('mfa-token-required', mfaParmameters)
    },

    missingInfoRequired({ requiredFields }) {
      this.$refs.modal.close()
      this.$emit('missing-info-required', { requiredFields })
    },

    switchTab(tab) {
      this.activeTab = tab
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~friendship_styles/colors" as colors;

.authentication-header {
  ::v-deep(form:not(:last-child)) {
    margin-bottom: 20px;
  }
}

::v-deep(.modal-body) {
  padding: 0;
}

::v-deep(.nav-tabs) {
  border: 0;

  li {
    flex-grow: 1;
    text-align: center;
  }

  .nav-link{
    border: solid 1px colors.$separator-gray;
    border-top-width: 6px;
    border-radius: 0;
    background-color: #f7f7f7;
    font-size: 16px;
    font-weight: 500;
    padding: {
      top: 15px;
      bottom: 15px;
    }

    &.active {
      border-width: 6px 0 0 0;
      background-color: #ffffff;
    }
  }
}
</style>
